'use strict';

/**
 * Client-side SIRET number validation.
 *
 * Deliberate mirror of the server helper
 * `app_storefront_hf/cartridge/scripts/helpers/siretNumberHelper.js`: identical
 * logic kept in sync by hand so the storefront gives immediate, network-free
 * feedback (no ajax round-trip) while the server keeps the authoritative check.
 * Both files are pure JS (no `dw/*`), so the two stay byte-for-byte aligned —
 * any change here MUST be applied to the server twin (and its unit test), and
 * vice versa.
 *
 * See the server twin for the validation rules and the INSEE exceptions.
 */

var SEPARATORS = /[\s.-]/g;
var SIRET_PATTERN = /^[0-9]{14}$/;
var LA_POSTE_SIREN = '356000000';
var ZERO_SIREN = '000000000';

/**
 * Normalizes a raw SIRET: removal of spaces, dots and dashes.
 * @param {string} value - Raw SIRET as entered by the user.
 * @returns {string} Normalized SIRET (may be empty).
 */
function normalize(value) {
    return String(value || '').replace(SEPARATORS, '');
}

/**
 * Checks the structural format of a normalized SIRET.
 * @param {string} normalized - Normalized SIRET (no separators).
 * @returns {boolean} True when the value is exactly 14 digits.
 */
function hasValidFormat(normalized) {
    return SIRET_PATTERN.test(normalized);
}

/**
 * Luhn checksum: reading right to left, every second digit is doubled (and
 * reduced by 9 when it exceeds 9); the total must be a multiple of 10.
 * @param {string} digits - Digits-only string to check.
 * @returns {boolean} True when the Luhn checksum is satisfied.
 */
function hasValidLuhnChecksum(digits) {
    var sum = 0;
    var shouldDouble = false;
    var digit;
    var i;

    for (i = digits.length - 1; i >= 0; i--) {
        digit = parseInt(digits.charAt(i), 10);
        if (shouldDouble) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }
        sum += digit;
        shouldDouble = !shouldDouble;
    }

    return sum % 10 === 0;
}

/**
 * Applies the INSEE rule reserved to La Poste: the sum of the 14 digits of the
 * SIRET must be a multiple of 5.
 * @param {string} digits - Normalized SIRET (exactly 14 digits).
 * @returns {boolean} True when the digit sum is a multiple of 5.
 */
function hasValidLaPosteChecksum(digits) {
    var sum = 0;
    var i;

    for (i = 0; i < digits.length; i++) {
        sum += parseInt(digits.charAt(i), 10);
    }

    return sum % 5 === 0;
}

/**
 * Validates the control keys of a well-formed SIRET.
 *
 * Luhn runs twice, independently: the doubling parity differs between a 9-digit and a
 * 14-digit run, so a value can satisfy one and fail the other. For La Poste the INSEE
 * digit-sum rule is an ADDITIONAL acceptance path, never a replacement, its head office
 * (35600000000048) satisfying Luhn but failing the digit sum.
 * @param {string} normalized - Normalized SIRET (exactly 14 digits).
 * @returns {boolean} True when the control keys are consistent.
 */
function hasValidChecksum(normalized) {
    var siren = normalized.substring(0, 9);

    // Luhn-neutral (digit sum 0) but never assigned by INSEE: would let 14 zeros through.
    if (siren === ZERO_SIREN || !hasValidLuhnChecksum(siren)) {
        return false;
    }
    if (siren === LA_POSTE_SIREN) {
        return hasValidLuhnChecksum(normalized) || hasValidLaPosteChecksum(normalized);
    }

    return hasValidLuhnChecksum(normalized);
}

/**
 * Validates a SIRET number.
 * @param {string} rawSiret - Raw SIRET as entered by the user.
 * @returns {{valid: boolean, normalized: string, reason: string}} Validation result.
 *  `reason` is one of: 'empty', 'format', 'checksum', '' (valid).
 */
function validate(rawSiret) {
    var normalized = normalize(rawSiret);

    if (!normalized) {
        return { valid: false, normalized: '', reason: 'empty' };
    }
    if (!hasValidFormat(normalized)) {
        return { valid: false, normalized: normalized, reason: 'format' };
    }
    if (!hasValidChecksum(normalized)) {
        return { valid: false, normalized: normalized, reason: 'checksum' };
    }

    return { valid: true, normalized: normalized, reason: '' };
}

module.exports = {
    normalize: normalize,
    hasValidFormat: hasValidFormat,
    hasValidLuhnChecksum: hasValidLuhnChecksum,
    hasValidLaPosteChecksum: hasValidLaPosteChecksum,
    hasValidChecksum: hasValidChecksum,
    validate: validate
};
